// src/components/gestionEvento/CustomPruebaModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  MenuItem,
  Tooltip,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  FormControlLabel,
  Switch,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import CategoriaChips from './CategoriaChips';

// --- helpers de tipo de marca ---
const testTypeToTipoMarca = {
  Planos: 'velocidad',
  Steeplechase: 'medioFondo',
  Relevos: 'medioFondo',
  Saltos: 'campo',
  Lanzamientos: 'campo',
  Combinados: 'combinadas',
};

const getSuffix = (testType, numericValue) => {
  const value = parseInt(numericValue, 10) || 0;
  switch (testType) {
    case 'Vallas': return 'H';
    case 'Steeplechase': return 'S';
    case 'Caminata': return 'W';
    case 'Relevos':
      if (value >= 3200) return 'D';
      if (value > 1600) return 'S';
      return '';
    default: return '';
  }
};

const determineTipoMarca = (testType, numericValue) => {
  const num = numericValue;
  switch (testType) {
    case 'Vallas': return num <= 300 ? 'velocidad' : 'medioFondo';
    case 'Carrera':
      if (num > 5000) return 'fondo';
      if (num > 300) return 'medioFondo';
      return 'velocidad';
    case 'Caminata': return num >= 5000 ? 'fondo' : 'medioFondo';
    case 'Saltos':
    case 'Lanzamientos': return 'campo';
    case 'Combinados': return 'combinadas';
    default: return testTypeToTipoMarca[testType] || '';
  }
};

// defensas pequeñas
const safe = (v, d) => (v === undefined || v === null ? d : v);
const ensureNotesObject = (raw) => {
  if (raw && typeof raw === 'object') return { ...raw };
  if (typeof raw === 'string') return { nota: raw };
  return {};
};

const CustomPruebaModal = ({
  isOpen,
  onClose,
  onSave,
  pruebaData: pruebaDataProp,
  onDataChange,
  categorias,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // fallback por si llega null
  const pruebaData = safe(pruebaDataProp, {
    id: '',
    nombre: '',
    testType: '',
    eventCode: '',
    tipoMarca: '',
    categoriaIdsFemenil: [],
    categoriaIdsVaronil: [],
    descripcion: '',
    notesByCategoria: {},
  });

  const [displayValue, setDisplayValue] = useState('');
  const [currentTestType, setCurrentTestType] = useState(pruebaData.testType || '');

  const isPruebaPista = ['Planos', 'Carrera', 'Vallas', 'Steeplechase', 'Caminata', 'Relevos'].includes(currentTestType);
  const isPruebaCampo = ['Saltos', 'Lanzamientos', 'Combinados'].includes(currentTestType);

  // ¿Hay notas guardadas?
  const hasAnyNote = useMemo(() => {
    const notes = pruebaData?.notesByCategoria || {};
    return Object.values(notes).some(byCat =>
      ['F', 'M'].some(r => {
        const o = ensureNotesObject(byCat?.[r]);
        return o.nota && String(o.nota).trim() !== '';
      })
    );
  }, [pruebaData?.notesByCategoria]);

  const [showNotesByCat, setShowNotesByCat] = useState(false);

  useEffect(() => {
    if (isOpen) setShowNotesByCat(hasAnyNote);
  }, [isOpen, hasAnyNote]);

  // sincronizar tipo/eventCode y displayValue
  useEffect(() => {
    const tt = pruebaData?.testType || '';
    setCurrentTestType(tt);

    if (isPruebaPista) {
      const m = pruebaData?.eventCode?.match(/^(\d+)/);
      setDisplayValue(m ? m[1] : '');
    } else {
      setDisplayValue(pruebaData?.eventCode || '');
    }

    const numericValue = isPruebaPista
      ? parseInt(pruebaData?.eventCode?.match(/^(\d+)/)?.[1] || '0', 10)
      : 0;

    const newTipoMarca = determineTipoMarca(tt, numericValue);
    if (pruebaData?.tipoMarca !== newTipoMarca) {
      onDataChange({ ...pruebaData, tipoMarca: newTipoMarca });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pruebaData?.testType, pruebaData?.eventCode]);

  const handleFieldChange = (field, value) => {
    const newData = { ...pruebaData };
    if (field === 'testType') {
      const tipoMarca = determineTipoMarca(value, 0);
      newData.testType = value;
      newData.eventCode = '';
      newData.tipoMarca = tipoMarca;
      setCurrentTestType(value);
      setDisplayValue('');
    } else if (field === 'eventCode') {
      if (isPruebaPista) {
        const numericValue = String(value || '').replace(/\D/g, '');
        const suffix = getSuffix(newData.testType, numericValue);
        const num = parseInt(numericValue, 10) || 0;
        newData.eventCode = numericValue ? numericValue + suffix : '';
        newData.tipoMarca = determineTipoMarca(newData.testType, num);
        setDisplayValue(numericValue);
      } else {
        newData.eventCode = String(value || '').toUpperCase();
        newData.tipoMarca = determineTipoMarca(newData.testType, 0);
        setDisplayValue(newData.eventCode);
      }
    } else {
      newData[field] = value;
    }
    onDataChange(newData);
  };

  // upsert de nota por categoría/rama
  const upsertByCatRama = (catId, rama, partial) => {
    onDataChange((prev) => {
      const base = typeof prev === 'function' ? prev(pruebaData) : (prev || pruebaData);
      const notes = { ...(base.notesByCategoria || {}) };
      const byCat = ensureNotesObject(notes[catId]);
      const byRama = ensureNotesObject(byCat[rama]);
      const merged = { ...byRama, ...partial };
      byCat[rama] = merged;
      notes[catId] = byCat;
      return { ...base, notesByCategoria: notes };
    });
  };

  // mapas y pares seleccionados
  const catMapById = useMemo(() => {
    const map = {};
    (categorias || []).forEach((c) => (map[c.id] = c));
    return map;
  }, [categorias]);

  const femPairs = useMemo(
    () => (pruebaData?.categoriaIdsFemenil || []).map(id => ({ id, rama: 'F' })),
    [pruebaData?.categoriaIdsFemenil]
  );
  const varPairs = useMemo(
    () => (pruebaData?.categoriaIdsVaronil || []).map(id => ({ id, rama: 'M' })),
    [pruebaData?.categoriaIdsVaronil]
  );

  const sortByCatThenName = (a, b) => {
    const aNum = catMapById[a.id]?.catNumber ?? Number.POSITIVE_INFINITY;
    const bNum = catMapById[b.id]?.catNumber ?? Number.POSITIVE_INFINITY;
    if (aNum !== bNum) return aNum - bNum;
    const aName = catMapById[a.id]?.nombre || '';
    const bName = catMapById[b.id]?.nombre || '';
    return aName.localeCompare(bName);
  };

  const selectedPairsSorted = useMemo(() => {
    const femSorted = [...femPairs].sort(sortByCatThenName);
    const varSorted = [...varPairs].sort(sortByCatThenName);
    return [...femSorted, ...varSorted];
  }, [femPairs, varPairs]);

  const dynamicFieldLabel = isPruebaPista ? 'Valor numérico (metros)' : (isPruebaCampo ? 'Código de Evento' : 'Identificador');
  const dynamicFieldTooltip = isPruebaPista
    ? 'Distancia en metros para pruebas de pista. Ej: 100, 200, etc.'
    : 'Código estándar para eventos de campo. Ej: LJ=Salto Largo, DT=Disco, etc.';
  const eventCodeExamples = {
    Saltos: 'LJ (Long Jump), TJ (Triple Jump), HJ (High Jump), PV (Pole Vault)',
    Lanzamientos: 'SP (Shot Put), DT (Discus Throw), JT (Javelin Throw), HT (Hammer Throw)',
    Combinados: 'DEC (Decathlon), HEP (Heptathlon), PEN (Pentathlon)',
    Relevos: '4x100=400R, 4x400=1600R',
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="custom-prueba-dialog"
      fullScreen={isMobile}          // 👈 full-screen solo en móvil
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: isMobile
          ? {
              m: 0,
              height: '100dvh',
              maxHeight: '100dvh',
              width: '100%',
              maxWidth: '100%',
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
            }
          : {
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
            },
      }}
    >
      {/* Header sticky */}
      <DialogTitle
        id="custom-prueba-dialog-title"
        sx={(t) => ({
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: t.palette.background.paper,
          borderBottom: `1px solid ${t.palette.divider}`,
          pr: 6,
          pl: 6,
        })}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, textAlign: 'left' }}>
          {pruebaData?.id ? 'Editar Prueba' : 'Crear Prueba Personalizada'}
        </Typography>

        <IconButton
          aria-label="cerrar"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Body scrollable */}
      <DialogContent
        sx={(t) => ({
          flex: 1,
          overflowY: 'auto',
          pt: 2,
          pb: 2,
          px: { xs: 2, sm: 3 },
          backgroundImage: `linear-gradient(${alpha(t.palette.background.paper, 1)}, ${alpha(
            t.palette.background.paper,
            1
          )})`,
        })}
      >
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* Nombre */}
          <Grid item xs={12}>
            <Tooltip title="Escribe el nombre como quieres que se muestre en listados y resultados" arrow>
              <TextField
                label="Nombre de la Prueba"
                fullWidth
                value={pruebaData?.nombre || ''}
                onChange={(e) => handleFieldChange('nombre', e.target.value)}
                variant="outlined"
                size="small"
                helperText="Ej: '100 metros planos' o 'Salto de longitud'"
              />
            </Tooltip>
          </Grid>

          {/* Tipo */}
          <Grid item xs={12} sm={6}>
            <Tooltip title="Selecciona el tipo de prueba para determinar cálculos automáticos" arrow>
              <TextField
                label="Tipo de Prueba"
                select
                fullWidth
                value={pruebaData?.testType || ''}
                onChange={(e) => handleFieldChange('testType', e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value="">Seleccionar tipo...</MenuItem>
                <MenuItem value="Planos">Planos (Velocidad)</MenuItem>
                <MenuItem value="Carrera">Carrera (Medio/Fondo)</MenuItem>
                <MenuItem value="Vallas">Vallas</MenuItem>
                <MenuItem value="Steeplechase">Steeplechase</MenuItem>
                <MenuItem value="Caminata">Caminata</MenuItem>
                <MenuItem value="Relevos">Relevos</MenuItem>
                <MenuItem value="Saltos">Saltos</MenuItem>
                <MenuItem value="Lanzamientos">Lanzamientos</MenuItem>
                <MenuItem value="Combinados">Pruebas Combinadas</MenuItem>
              </TextField>
            </Tooltip>
          </Grid>

          {/* EventCode */}
          <Grid item xs={12} sm={6}>
            <Tooltip title={dynamicFieldTooltip} arrow>
              <TextField
                label={dynamicFieldLabel}
                type={isPruebaPista ? 'number' : 'text'}
                fullWidth
                value={displayValue}
                onChange={(e) => handleFieldChange('eventCode', e.target.value)}
                InputProps={{
                  endAdornment: isPruebaPista && (
                    <InputAdornment position="end">
                      {getSuffix(currentTestType, displayValue)}
                    </InputAdornment>
                  ),
                  inputProps: {
                    style: { textTransform: isPruebaCampo ? 'uppercase' : 'none' },
                  },
                }}
                variant="outlined"
                size="small"
                helperText={
                  isPruebaCampo &&
                  `Ejemplos: ${eventCodeExamples[currentTestType] || 'Usar códigos estándar'}`
                }
              />
            </Tooltip>
          </Grid>

          {/* Chips de categorías */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Categorías Femenil</Typography>
            <CategoriaChips
              categorias={categorias}
              selectedCategorias={pruebaData?.categoriaIdsFemenil || []}
              setSelectedCategorias={(selected) => handleFieldChange('categoriaIdsFemenil', selected)}
              allLabel="Todas las categorías Femenil"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Categorías Varonil</Typography>
            <CategoriaChips
              categorias={categorias}
              selectedCategorias={pruebaData?.categoriaIdsVaronil || []}
              setSelectedCategorias={(selected) => handleFieldChange('categoriaIdsVaronil', selected)}
              allLabel="Todas las categorías Varonil"
            />
          </Grid>

          {/* Descripción global */}
          <Grid item xs={12}>
            <TextField
              label="Descripción global (opcional)"
              fullWidth
              multiline
              minRows={2}
              value={pruebaData?.descripcion || ''}
              onChange={(e) => handleFieldChange('descripcion', e.target.value)}
              variant="outlined"
              size="small"
              placeholder="Notas generales de la prueba (no específicas por categoría/rama)"
            />
          </Grid>

          {/* Toggle + acordeones de notas */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <div>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Notas por categoría y Rama
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pesos de implementos o alturas de vallas.
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <Switch
                    checked={showNotesByCat}
                    onChange={(e) => setShowNotesByCat(e.target.checked)}
                  />
                }
                label="Editar notas"
              />
            </Stack>
          </Grid>

          {showNotesByCat && (femPairs.length + varPairs.length) > 0 && (
            <Grid item xs={12}>
              <Stack spacing={1}>
                {selectedPairsSorted.map(({ id, rama }) => {
                  const cat = catMapById[id];
                  const byCat = ensureNotesObject(pruebaData?.notesByCategoria?.[id]);
                  const byRama = ensureNotesObject(byCat?.[rama]);
                  const { nota = '' } = byRama;

                  return (
                    <Accordion key={`${id}-${rama}`} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 700, flex: 1, mr: 1,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}
                          >
                            {cat ? cat.nombre : 'Categoría'}
                          </Typography>
                          <Chip size="small" label={rama === 'F' ? 'Femenino' : 'Masculino'} variant="outlined" />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField
                          label="Nota (opcional)"
                          fullWidth
                          multiline
                          minRows={2}
                          value={nota}
                          onChange={(e) => upsertByCatRama(id, rama, { nota: e.target.value })}
                          variant="outlined"
                          size="small"
                          placeholder="Ej: 0.91 m; vallas U18 / 7.26 kg; bala adultos / Viento máx. +2.0…"
                        />
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {/* Footer sticky — botones juntos a la derecha con el mismo estilo */}
      <DialogActions
        sx={(t) => ({
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          bgcolor: t.palette.background.paper,
          borderTop: `1px solid ${t.palette.divider}`,
          py: 1.5,
          px: { xs: 2, sm: 3 },
          justifyContent: 'flex-end',
          gap: 2,
        })}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={(t) => ({
            borderRadius: 999,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
            color: t.palette.secondary.main,
            border: `1px solid ${t.palette.secondary.main}`,
            '&:hover': { bgcolor: t.palette.action.hover },
          })}
        >
          Cancelar
        </Button>
        <Button
          onClick={() => { onSave(); onClose(); }}
          variant="contained"
          disabled={!pruebaData?.nombre || !pruebaData?.testType}
          sx={{
            borderRadius: 999,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          {pruebaData?.id ? 'Actualizar Prueba' : 'Crear Prueba'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CustomPruebaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  pruebaData: PropTypes.shape({
    id: PropTypes.string,
    nombre: PropTypes.string,
    testType: PropTypes.string,
    eventCode: PropTypes.string,
    tipoMarca: PropTypes.string,
    categoriaIdsFemenil: PropTypes.arrayOf(PropTypes.string),
    categoriaIdsVaronil: PropTypes.arrayOf(PropTypes.string),
    descripcion: PropTypes.string,
    notesByCategoria: PropTypes.object,
  }),
  onDataChange: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  categorias: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
      catNumber: PropTypes.number,
    })
  ).isRequired,
};

export default CustomPruebaModal;
